import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Tag, Typography, Space, Button, Input, message, Divider } from 'antd';
import apiService from '@/services/apiService';
import { ReloadOutlined } from '@ant-design/icons';

type IssueRow = {
  id: number;           // issue id
  storeName?: string;   // store name
  error: string;        // message
  code?: string;        // error code
  severity: 'error' | 'warn' | 'info';
  status: 'open' | 'resolved';
  at: string;           // ISO time
};

const mapSeverity = (code?: string): IssueRow['severity'] => {
  const c = String(code || '').toUpperCase();
  
  // Shopify specific error mapping
  if (c.includes('MISSING') || c.includes('UNAUTHORIZED') || c.includes('FAILED')) return 'error';
  if (c.includes('TIMEOUT') || c.includes('RATE') || c.includes('LIMIT')) return 'warn';
  if (c.includes('HTTP') || c.includes('NETWORK')) return 'error';
  
  return 'error';
};

const severityColor: Record<IssueRow['severity'], string> = {
  error: 'red',
  warn: 'orange',
  info: 'blue',
};


const ShopifyIssues: React.FC = () => {
  const [rows, setRows] = useState<IssueRow[]>([]);
  const [query, setQuery] = useState(''); // 实际用于搜索的值
  const [searchInput, setSearchInput] = useState(''); // 输入框的值
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // 默认10条
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchIssues = useCallback(async () => {
    try {
      setLoading(true);
      const resp: any = await apiService.get('/shopify/issues', {
        params: { q: query || undefined, page, limit: pageSize }
      });
      if (!resp?.success) throw new Error(resp?.message || 'Failed to load issues');
      const list: Array<{ id: number; storeName?: string; errorCode: string; message: string; occurredAt: string }>
        = Array.isArray(resp?.data) ? resp.data : (resp?.rows || []);
      const mapped: IssueRow[] = (list || []).map((r: any) => ({
        id: r.id,
        storeName: r.storeName,
        error: r.message,
        code: r.errorCode,
        severity: mapSeverity(r.errorCode),
        status: 'open',
        at: r.occurredAt,
      }));
      setRows(mapped);
      const p = resp?.pagination || {};
      setTotal(Number(p.total || mapped.length));
    } catch (e) {
      console.error('Load issues failed:', e);
      message.error('無法載入 Shopify 錯誤列表');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query, page, pageSize]);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);


  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title="Shopify 錯誤列表">
        <Space style={{ marginBottom: 12 }} wrap>
          <Input.Search 
            placeholder="搜尋 商店名稱 / 錯誤 / 代碼" 
            allowClear 
            value={searchInput}
            onChange={(e)=> setSearchInput(e.target.value)}
            onSearch={(v)=>{ setPage(1); setQuery(v); }}
            style={{ width: 320 }} 
          />
          <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchIssues}>
            刷新
          </Button>
        </Space>

        <Table rowKey="id" dataSource={rows} loading={loading}
          pagination={{ 
            current: page, 
            pageSize, 
            total, 
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 200],
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 條，共 ${total} 條記錄`,
            onChange:(p, ps)=>{ 
              setPage(p); 
              if (ps && ps !== pageSize) {
                setPageSize(ps);
                setPage(1); // 改变页大小时重置到第一页
              }
            } 
          }}
          rowClassName={(r)=> r.severity==='error' && r.status==='open' ? 'row-danger' : ''}
          columns={[
            { title: '商店名稱', dataIndex: 'storeName', width: 160 },
            { title: '錯誤訊息', dataIndex: 'error', render:(v:string, rec:IssueRow)=> (
              <Space>
                <Tag color={severityColor[rec.severity]}>{rec.code || rec.severity.toUpperCase()}</Tag>
                <Typography.Text>{v}</Typography.Text>
              </Space>
            )},
            { title: '時間', dataIndex: 'at', width: 200, render:(v:string)=> new Date(v).toLocaleString() },
            // 簡化：去掉狀態與操作欄，僅展示必要資訊
          ]}
        />
        <Divider />
        <Typography.Paragraph type="secondary">本頁列出 Shopify 同步、Webhook 或 API 調用時的錯誤；可重試、標記已處理或查看原始資料。</Typography.Paragraph>
      </Card>

      <style>{`.row-danger td{background:#fff1f0!important;}`}</style>
    </Space>
  );
};

export default ShopifyIssues;
